import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/apiConfig';
import './SignatureAudit.css';

const SignatureAudit = ({ employeeId }) => {
  const [signatures, setSignatures] = useState([]);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  

  useEffect(() => {
    fetchSignatures();
  }, [employeeId]);

  const fetchSignatures = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = employeeId 
        ? `${API_URL}/audit/signatures?employeeId=${employeeId}`
        : `${API_URL}/audit/signatures`;
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch signatures');
      }
      
      const data = await response.json();
      setSignatures(data.signatures || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSignatureDetails = async (signatureId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/audit/signatures/${signatureId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch signature details');
      }
      
      const data = await response.json();
      setSelectedSignature(data.signature);
    } catch (err) {
      console.error('Error fetching signature details:', err);
    }
  };

  const downloadCertificate = async (signatureId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/audit/signatures/${signatureId}/certificate`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download certificate');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signature-certificate-${signatureId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading certificate:', err);
    }
  };

  if (loading) {
    return <div className="audit-loading">Loading signature audit trail...</div>;
  }

  if (error) {
    return <div className="audit-error">Error: {error}</div>;
  }

  return (
    <div className="signature-audit-container">
      <h2>Document Signature Audit Trail</h2>
      
      <div className="signatures-list">
        <table className="audit-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Document Type</th>
              <th>Signer</th>
              <th>Signed Date</th>
              <th>IP Address</th>
              <th>Device</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {signatures.map(sig => (
              <tr key={sig.id}>
                <td>{sig.id}</td>
                <td>{sig.documentType}</td>
                <td>{sig.signerFirstName} {sig.signerLastName}</td>
                <td>{new Date(sig.signedAt).toLocaleString()}</td>
                <td>{sig.ipAddress}</td>
                <td>{sig.deviceType}</td>
                <td>
                  <button 
                    onClick={() => fetchSignatureDetails(sig.id)}
                    className="view-details-btn"
                  >
                    View Details
                  </button>
                  <button 
                    onClick={() => downloadCertificate(sig.id)}
                    className="download-cert-btn"
                  >
                    Certificate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedSignature && (
        <div className="signature-details-modal">
          <div className="modal-content">
            <h3>Signature Details</h3>
            <span className="close" onClick={() => setSelectedSignature(null)}>&times;</span>
            
            <div className="detail-section">
              <h4>Signer Information</h4>
              <p><strong>Name:</strong> {selectedSignature.signerFirstName} {selectedSignature.signerLastName}</p>
              <p><strong>Email:</strong> {selectedSignature.signerEmail || 'Not provided'}</p>
              <p><strong>Employee ID:</strong> {selectedSignature.employeeId}</p>
            </div>

            <div className="detail-section">
              <h4>Technical Details</h4>
              <p><strong>IP Address:</strong> {selectedSignature.ipAddress}</p>
              <p><strong>User Agent:</strong> <code>{selectedSignature.userAgent}</code></p>
              <p><strong>Device Type:</strong> {selectedSignature.deviceType}</p>
              {selectedSignature.browserInfo && (
                <>
                  <p><strong>Browser:</strong> {selectedSignature.browserInfo.browser}</p>
                  <p><strong>OS:</strong> {selectedSignature.browserInfo.os}</p>
                </>
              )}
            </div>

            <div className="detail-section">
              <h4>Legal Compliance</h4>
              <p><strong>Consent Given:</strong> {selectedSignature.legalConsentGiven ? 'Yes' : 'No'}</p>
              <p><strong>Signed At:</strong> {new Date(selectedSignature.signedAt).toLocaleString()}</p>
              {selectedSignature.metadata && selectedSignature.metadata.timezone && (
                <p><strong>Timezone:</strong> {selectedSignature.metadata.timezone}</p>
              )}
            </div>

            <div className="detail-section">
              <h4>Consent Text</h4>
              <p className="consent-text">{selectedSignature.consentText}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureAudit;